import React, { useMemo, useState } from "react";
import { Switch, Route, useRouteMatch } from "react-router-dom";

import { StyledTitle } from "../../styles";
import { Col } from "react-bootstrap";
import { PubRow, StyledImage, ProTitle } from "./styles";
import Materials from "../../components/Materials";
import Authors from "../../components/Authors";
import Project from "./Project";
import { Button, Stack } from "react-bootstrap";
import { Helmet } from "react-helmet";

const ProjectTeaser = ({ project, people }) => {
  let projectLoc = project.link ? project.link : "/projects/" + project.urlId;
  return (
    <PubRow key={project.urlId}>
      <Col xs={6} sm={3} md={2}>
        <a href={process.env.PUBLIC_URL + projectLoc}>
          {project.teaserImg ? (
            <StyledImage
              src={
                process.env.PUBLIC_URL +
                "/Image/paper/teaser/" +
                project.teaserImg
              }
              thumbnail
            />
          ) : (
            <StyledImage
              src={process.env.PUBLIC_URL + "/Image/default4-3.svg"}
              thumbnail
            />
          )}
        </a>
      </Col>
      <Col xs={12} sm={9} md={8}>
        <ProTitle href={process.env.PUBLIC_URL + projectLoc}>
          {" "}
          {project.title}
        </ProTitle>
        <p>
          <Authors names={project.authors} people={people} />
        </p>
        <p>{project.shortDesc}</p>
        <p>
          <Materials materials={project.materials} />
        </p>
      </Col>
    </PubRow>
  );
};

const Projects = ({ papers, people, projects }) => {
  let { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={path}>
        <StyledTitle id="projects"> Projects</StyledTitle>
        {projects.map((pro, index) => (
          <ProjectTeaser key={index} project={pro} people={people} />
        ))}
      </Route>
      <Route path={`${path}/add`}>
        <AddEditProject projects={projects} />
      </Route>
      <Route path={`${path}/:projectId`}>
        <Project projects={projects} people={people} papers={papers} />
      </Route>
    </Switch>
  );
};

export default Projects;

const copyToClipboard = async (text) => {
  await navigator.clipboard.writeText(text);
};

const AddEditProject = ({ projects = [] }) => {
  const [outputJson, setOutputJson] = useState(null);
  const [agree, setAgree] = useState(false);
  const [mode, setMode] = useState("add"); // 'add' | 'edit'
  const [selectedKey, setSelectedKey] = useState(""); // urlId
  const [prefill, setPrefill] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const options = useMemo(() => {
    return (projects || [])
      .map((p) => ({ key: p.urlId, label: `${p.title} — ${p.urlId}`, project: p }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projects]);

  function switchMode(next) {
    setMode(next);
    if (next === "add") {
      setPrefill(null);
      setSelectedKey("");
      setFormKey((k) => k + 1);
    }
  }

  function handleSelectExisting(e) {
    const key = e.target.value;
    setSelectedKey(key);
    const found = options.find((o) => o.key === key);
    if (!found) {
      setPrefill(null);
      return;
    }
    const p = found.project;
    const authors = (p.authors || []).join(", ");
    const citations = (p.citations || []).join(
      ", "
    );
    let teaserUrl = "";
    if (p.teaserImg && p.teaserImg.includes("—file at:")) {
      teaserUrl = p.teaserImg.split("—file at:").pop();
    }
    setPrefill({
      urlId: p.urlId || "",
      title: p.title || "",
      shortDesc: p.shortDesc || "",
      authors,
      citations,
      teaserUrl,
      materials: p.materials || {},
      previousUrlId: p.urlId || "",
    });
    setFormKey((k) => k + 1);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const raw = Object.fromEntries(formData.entries());

    const authors = (raw.authors || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const citations = (raw.citations || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const materials = {};
    ["Homepage", "Code", "Demo", "Video", "Paper"].forEach((k) => {
      const v = raw[`materials_${k}`] || "";
      if (v.trim() !== "") materials[k] = v.trim();
    });

    const teaserId = (raw.urlId || "").trim();
    let teaserImg = "";
    if (mode === "edit" && prefill && (!raw.teaserUrl || raw.teaserUrl.trim() === "")) {
      const existing = options.find((o) => o.key === selectedKey)?.project;
      if (existing && existing.teaserImg) teaserImg = existing.teaserImg;
      else if (teaserId !== "") teaserImg = `${teaserId}.png`;
    } else {
      if (teaserId !== "") teaserImg = `${teaserId}.png`;
      if ((raw.teaserUrl || "").trim() !== "") {
        teaserImg = teaserImg + "—file at:" + raw.teaserUrl.trim();
      }
    }

    const project = {
      urlId: raw.urlId || "",
      title: raw.title || "",
      teaserImg,
      shortDesc: raw.shortDesc || "",
      authors,
      materials,
      citations,
      layout: [],
    };

    const payload =
      mode === "edit"
        ? { action: "update", previousUrlId: prefill?.previousUrlId || project.urlId, ...project }
        : project;

    setOutputJson(payload);
  }

  const jsonString = outputJson ? JSON.stringify(outputJson, null, 2) : "";

  return (
    <div>
      <h3>Add or edit a project</h3>
      <Helmet>
        <title>Add or edit a project - Khoury Vis Lab</title>
        <meta name="description" content="Add or edit a project" />
      </Helmet>

      <div style={{ marginBottom: 12 }}>
        <label>
          <input
            type="radio"
            name="mode"
            value="add"
            checked={mode === "add"}
            onChange={() => switchMode("add")}
          />{" "}
          Add
        </label>{" "}
        <label style={{ marginLeft: 16 }}>
          <input
            type="radio"
            name="mode"
            value="edit"
            checked={mode === "edit"}
            onChange={() => switchMode("edit")}
          />{" "}
          Edit
        </label>
      </div>

      {mode === "edit" && (
        <div style={{ marginBottom: 12 }}>
          <label>
            <strong>Select existing project</strong>
            <br />
            <select value={selectedKey} onChange={handleSelectExisting}>
              <option value="">-- choose --</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <form key={formKey} method="post" onSubmit={handleSubmit}>
        <hr />
        <h4>Required fields</h4>
        <p>
          <label>
            <strong>URL ID</strong>
            <br />
            <textarea name="urlId" rows={1} cols={120} defaultValue={prefill?.urlId || ""} />
          </label>
        </p>
        <p>
          <label>
            <strong>Title</strong>
            <br />
            <textarea name="title" rows={1} cols={120} defaultValue={prefill?.title || ""} />
          </label>
        </p>
        <p>
          <label>
            <strong>Short description</strong>
            <br />
            <textarea name="shortDesc" rows={3} cols={120} defaultValue={prefill?.shortDesc || ""} />
          </label>
        </p>
        <p>
          <label>
            <strong>Authors</strong> — comma-separated
            <br />
            <textarea name="authors" rows={1} cols={120} defaultValue={prefill?.authors || ""} />
          </label>
        </p>

        <h4>Images</h4>
        <p>
          <label>
            <strong>Teaser image URL</strong> — 640x360 PNG
            <br />
            <textarea name="teaserUrl" rows={1} cols={120} defaultValue={prefill?.teaserUrl || ""} />
          </label>
        </p>

        <h4>Materials (optional)</h4>
        {[
          "Homepage",
          "Code",
          "Demo",
          "Video",
          "Paper",
        ].map((k) => (
          <p key={k}>
            <label>
              <strong>{k}</strong>
              <br />
              <textarea name={`materials_${k}`} rows={1} cols={120} defaultValue={prefill?.materials?.[k] || ""} />
            </label>
          </p>
        ))}

        <h4>Optional</h4>
        <p>
          <label>
            <strong>Citations</strong> — comma-separated pub urlIds
            <br />
            <textarea name="citations" rows={1} cols={120} defaultValue={prefill?.citations || ""} />
          </label>
        </p>

        <hr />
        <h4>Submitting</h4>
        <label>
          <input
            type="checkbox"
            defaultChecked={false}
            checked={agree}
            onChange={() => setAgree(!agree)}
          />{' '}
          I have filled required fields and checked for accuracy.
        </label>
        <Button type="submit" className="mt-3 d-block" disabled={!agree}>
          Step 1: Generate JSON
        </Button>
      </form>

      {outputJson && (
        <div>
          <Stack direction="horizontal" gap={3} className="mt-2">
            <Button onClick={() => copyToClipboard(jsonString)}>
              Step 2: Copy to clipboard
            </Button>
            <Button
              className="mt-2"
              onClick={() => {
                const isEdit = mode === "edit";
                const baseUrl = isEdit
                  ? "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=update_project&projects=&title=[UPDATE PROJECT] "
                  : "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=new_project&projects=&template=new-project-template.md&title=[NEW PROJECT] ";
                const title = encodeURIComponent((outputJson.title || outputJson?.post?.title || ""));
                const url = baseUrl + title;
                window.open(url);
              }}
            >
              Step 3: Paste into new issue
            </Button>
          </Stack>
          <pre>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};
